// ============================================================
// STATE LOOKUP PANEL
// ------------------------------------------------------------
// Live State of Michigan record lookup for the six non-liquor
// license types. Calls /api/license-lookup, which runs the
// licensr-lookup Apify actor and meters usage server-side
// (so this panel must NOT also call checkAndConsumeQuota).
//
// Official-record facts only. Inferred values (e.g. skilled-
// trades status derived from the expiration date) are labelled
// as inferred, never shown as state-reported.
// ============================================================
import { useState } from 'react';

const FIELD_LABELS = {
  name: { label: 'Business / facility name', placeholder: 'Partial names are fine' },
  firstName: { label: 'First name', placeholder: '' },
  lastName: { label: 'Last name', placeholder: '' },
  licenseNumber: { label: 'License number', placeholder: 'Exact number, if you have it' },
  city: { label: 'City', placeholder: '' },
  county: { label: 'County', placeholder: '' },
};

const SLOW_NOTE = {
  'auto-repair': 'The Secretary of State site is slow. This search usually takes 25–40 seconds.',
};

const STATUS_STYLE = {
  active: { background: 'rgba(52, 211, 153, 0.15)', color: '#6ee7b7' },
  inactive: { background: 'rgba(248, 113, 113, 0.15)', color: '#fca5a5' },
  pending: { background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' },
  unknown: { background: 'rgba(100, 116, 139, 0.18)', color: '#cbd5e1' },
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtChecked(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function DisciplinaryItem({ item }) {
  if (typeof item === 'string') return <li style={s.discItem}>{item}</li>;
  if (!item || typeof item !== 'object') return null;
  const title = item.title || item.type || item.description || 'Record';
  const date = item.date || item.report_date;
  const url = item.url || item.view_url || item.download_url;
  return (
    <li style={s.discItem}>
      {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={s.link}>{title}</a> : title}
      {date ? <span style={s.muted}> · {fmtDate(date)}</span> : null}
    </li>
  );
}

function RecordCard({ r }) {
  const norm = r.status_normalized || 'unknown';
  const place = [r.address, r.city, [r.state, r.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const primary = r.business_name || r.name || r.dba || 'Unnamed licensee';
  const secondary = [r.business_name && r.name && r.name !== r.business_name ? r.name : '', r.dba ? `DBA ${r.dba}` : '']
    .filter(Boolean).join(' · ');
  const disc = Array.isArray(r.disciplinary) ? r.disciplinary : [];
  const facts = [
    ['License #', r.license_number],
    ['Type', r.license_type],
    ['Class', r.license_class],
    ['Issued', fmtDate(r.issue_date)],
    ['Expires', fmtDate(r.expiration_date)],
    ['Capacity', r.capacity],
    ['County', r.county],
    ['Phone', r.phone],
  ].filter(([, v]) => v);

  return (
    <div style={s.record}>
      <div style={s.recordHead}>
        <div style={{ minWidth: 0 }}>
          <div style={s.recordName}>{primary}</div>
          {secondary && <div style={s.muted}>{secondary}</div>}
          {place && <div style={s.muted}>{place}</div>}
        </div>
        <span style={{ ...s.statusPill, ...(STATUS_STYLE[norm] || STATUS_STYLE.unknown) }}>
          {r.status || norm}{r.status_inferred ? ' (inferred)' : ''}
        </span>
      </div>
      {r.status_inferred && r.status_note && <div style={s.inferred}>{r.status_note}</div>}
      <div style={s.facts}>
        {facts.map(([k, v]) => (
          <div key={k} style={s.fact}>
            <span style={s.factLabel}>{k}</span>
            <span>{v}</span>
          </div>
        ))}
      </div>
      {disc.length > 0 && (
        <div style={s.discBox}>
          <div style={s.factLabel}>State-posted reports / conditions ({disc.length})</div>
          <ul style={s.discList}>
            {disc.slice(0, 8).map((d, i) => <DisciplinaryItem key={i} item={d} />)}
          </ul>
          {disc.length > 8 && <div style={s.muted}>+{disc.length - 8} more on the state record</div>}
        </div>
      )}
      {r.detail_url && (
        <a href={r.detail_url} target="_blank" rel="noopener noreferrer" style={{ ...s.link, fontSize: '13px' }}>
          View the official state record ↗
        </a>
      )}
    </div>
  );
}

export default function StateLookupPanel({ type, user, onQuotaChange }) {
  const fields = type.lookupFields || ['name', 'licenseNumber', 'city'];
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState(null);

  const hasCriteria = fields.some((f) => (form[f] || '').trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!hasCriteria || loading) return;
    setLoading(true);
    setResp(null);
    try {
      const res = await fetch('/api/license-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: type.slug,
          ...Object.fromEntries(fields.map((f) => [f, (form[f] || '').trim()])),
        }),
      });
      const data = await res.json().catch(() => ({}));
      setResp(data);
    } catch {
      setResp({
        success: false,
        status: 'network',
        user_message: "We couldn't reach Licensr. Check your connection and try again.",
        state_link: type.deepLink,
      });
    } finally {
      setLoading(false);
      if (onQuotaChange) onQuotaChange();
    }
  };

  const results = resp?.results || [];

  return (
    <div style={s.wrap}>
      <form onSubmit={submit} style={s.form}>
        <div style={s.grid}>
          {fields.map((f) => (
            <label key={f} style={s.field}>
              <span style={s.label}>{FIELD_LABELS[f]?.label || f}</span>
              <input
                style={s.input}
                value={form[f] || ''}
                placeholder={FIELD_LABELS[f]?.placeholder || ''}
                onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))}
              />
            </label>
          ))}
        </div>
        <button type="submit" style={{ ...s.cta, opacity: hasCriteria && !loading ? 1 : 0.55 }} disabled={!hasCriteria || loading}>
          {loading ? 'Checking the state record…' : `Search ${type.agencyShort}`}
        </button>
        {loading && SLOW_NOTE[type.slug] && <p style={s.muted}>{SLOW_NOTE[type.slug]}</p>}
      </form>

      {resp && (
        <div style={s.results}>
          {resp.status === 'ok' && (
            <div style={s.summary}>
              {results.length === 0
                ? 'No matching record found in the state database.'
                : `${results.length} record${results.length === 1 ? '' : 's'} from ${resp.agency || type.agency}`}
              {resp.checked_at && (
                <span style={s.muted}> · checked {fmtChecked(resp.checked_at)}{resp.cached ? ' (saved result)' : ''}</span>
              )}
            </div>
          )}

          {resp.status !== 'ok' && (
            <div style={resp.status === 'quota_exceeded' ? s.warn : s.error}>
              {resp.user_message || resp.error || 'Something went wrong.'}
              {resp.state_link && resp.status !== 'quota_exceeded' && resp.status !== 'bad_input' && (
                <div style={{ marginTop: '8px' }}>
                  <a href={resp.state_link} target="_blank" rel="noopener noreferrer" style={s.link}>
                    Open the {type.agencyShort} site ↗
                  </a>
                </div>
              )}
            </div>
          )}

          {results.map((r, i) => <RecordCard key={`${r.license_number}-${i}`} r={r} />)}

          {resp.coverage_scope_note && <p style={s.coverage}>What this source covers: {resp.coverage_scope_note}</p>}
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { width: '100%', textAlign: 'left', marginTop: '12px' },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: 500, color: '#cbd5e1' },
  input: { background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(248, 250, 252, 0.15)', borderRadius: '8px', padding: '12px 14px', fontSize: '15px', color: '#f8fafc', outline: 'none', fontFamily: 'inherit' },
  cta: { alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '4px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#0f172a', padding: '14px 26px', fontSize: '15px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
  results: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '22px' },
  summary: { fontSize: '14px', color: '#f8fafc', fontWeight: 600 },
  muted: { fontSize: '13px', color: '#94a3b8', fontWeight: 400 },
  error: { background: 'rgba(248, 113, 113, 0.12)', border: '1px solid rgba(248, 113, 113, 0.3)', color: '#fecaca', padding: '12px 16px', borderRadius: '8px', fontSize: '14px' },
  warn: { background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', color: '#fbbf24', padding: '12px 16px', borderRadius: '8px', fontSize: '14px' },
  record: { background: 'rgba(15, 23, 42, 0.55)', border: '1px solid rgba(248, 250, 252, 0.1)', borderRadius: '12px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  recordHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' },
  recordName: { fontSize: '16px', fontWeight: 600, color: '#f8fafc', overflowWrap: 'anywhere' },
  statusPill: { flexShrink: 0, fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '20px', whiteSpace: 'nowrap' },
  inferred: { fontSize: '12px', color: '#fbbf24' },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 16px', fontSize: '14px', color: '#e2e8f0' },
  fact: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, overflowWrap: 'anywhere' },
  factLabel: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  discBox: { borderTop: '1px solid rgba(248, 250, 252, 0.08)', paddingTop: '10px' },
  discList: { margin: '6px 0 0', paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#e2e8f0' },
  discItem: { lineHeight: 1.4 },
  link: { color: '#7dd3fc', textDecoration: 'none' },
  coverage: { fontSize: '12px', color: '#64748b', lineHeight: 1.6, margin: '4px 0 0' },
};
